import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Button, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { memo, useCallback, useMemo, useState, Fragment } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { useDuplicateAsset } from 'components/Minimenus/hooks/useDuplicateAsset';
import { useAssetQuickLaunch } from 'components/editorial/AssetQuickLaunch/useAssetQuickLaunch';
import type { AssetQuickLaunchPrefill } from 'components/editorial/AssetQuickLaunch/types';
import { SimpleConfirmDeleteModal } from 'components/Modals/SimpleConfirmDeleteModal';
import { isAssetTypeEditorEnabled, toTitleCase } from 'utility/functions';
import { useCosmos } from 'providers/contexts/CosmosContext';
import {
  checkCreateAssetsPermission,
  checkDeleteAssetsPermission,
  checkSiteAdminPermission,
} from 'utility/permissions';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import type {
  ApiErrorResponse,
  BloxCustomDataProviderType,
  EditorialAssetSearchResultItem,
  PostEditorialAssetDeleteParams,
  PostEditorialAssetDeleteResult,
  PatchType,
} from 'utility/types/dataProvider';
import { MuiCloseReasons } from 'components/Minimenus/EditorialMiniMenu';
import { bloxValues } from 'themes/constants';

type TemplatesRowActionsProps = {
  record?: EditorialAssetSearchResultItem;
};

export const TemplatesRowActions = memo(({ record }: TemplatesRowActionsProps) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const { queryClient } = useCosmosQueryClient();
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const { sessionConfig } = useCosmos();
  const {
    openQuickLaunch,
    modal: quickLaunchModal,
    isQuickLaunchAssetType,
  } = useAssetQuickLaunch();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const { mutateAsync: mutateDelete, status: deleteStatus } = useMutation<
    PostEditorialAssetDeleteResult,
    ApiErrorResponse,
    PostEditorialAssetDeleteParams
  >({
    mutationKey: ['deleteTemplatesByIds'],
    mutationFn: async (params) => {
      return await dataProvider.deleteAssetsByIds(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['editorial/templates'] });
      // Keep the Create menu's Story Templates section in sync after a delete.
      queryClient.invalidateQueries({ queryKey: ['recentStoryTemplates'] });
    },
    onError: (error) => {
      notify(
        `${t('blox.general.error.label')}: ${error?.message || error?.axiosMessage}`,
        { type: 'error' },
      );
    },
  });

  const { duplicate, status: dupStatus } = useDuplicateAsset({
    invalidateQueries: true,
    onError: (err) => {
      console.error('[TemplatesRowActions] Duplicate error:', err);
      notify(`Error: ${err.message}`, { type: 'error' });
    },
  });

  const recordUuid = record?.uuid || '';
  const recordTitle = record?.title || '';
  const recordType = record?.type;
  const recordStatus = record?.status;

  const assetLabel = useMemo(() => {
    if (!recordType || typeof recordType !== 'string') {
      return 'Asset';
    }
    return toTitleCase(recordType);
  }, [recordType]);

  const canUseQuickLaunch = useMemo(() => {
    if (!recordType) {
      return false;
    }
    return isQuickLaunchAssetType(recordType);
  }, [isQuickLaunchAssetType, recordType]);

  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userCanCreate } = checkCreateAssetsPermission(sessionConfig);
  const { granted: userCanDelete, name: deletePermName } =
    checkDeleteAssetsPermission(sessionConfig);
  const hasCreatePerm = Boolean(userIsAdmin || userCanCreate);
  const hasDeletePerm = Boolean(userIsAdmin || userCanDelete);

  const editorEnabled = isAssetTypeEditorEnabled(recordType);
  const canDuplicate = Boolean(
    hasCreatePerm &&
    recordUuid &&
    recordStatus !== 'deleted' &&
    editorEnabled &&
    dupStatus !== 'pending',
  );
  const canDelete = Boolean(
    hasDeletePerm &&
    recordUuid &&
    recordStatus !== 'deleted' &&
    deleteStatus !== 'pending',
  );

  const openMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setAnchorEl(event.currentTarget);
  }, []);

  const closeMenu = useCallback((event?: any, reason?: MuiCloseReasons) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setAnchorEl(null);
  }, []);

  const runDuplicate = useCallback(
    async (prefill?: AssetQuickLaunchPrefill) => {
      if (!recordUuid || !recordTitle) {
        console.warn('[TemplatesRowActions] Duplicate missing uuid/title.');
        return;
      }

      await duplicate({
        id: recordUuid,
        title: recordTitle,
        quickLaunchPrefill: prefill,
      });

      queryClient.invalidateQueries({ queryKey: ['editorial/templates'] });
      notify('Asset duplicated.', { type: 'success' });
    },
    [duplicate, notify, queryClient, recordTitle, recordUuid],
  );

  const runUseTemplate = useCallback(
    async (prefill?: AssetQuickLaunchPrefill) => {
      if (!recordUuid || !recordTitle) {
        console.warn('[TemplatesRowActions] Use template missing uuid/title.');
        return;
      }

      const additionalPatches: PatchType[] = [
        {
          path: '/template',
          op: 'add',
          value: false,
        },
      ];

      await duplicate({
        id: recordUuid,
        title: recordTitle,
        quickLaunchPrefill: prefill,
        additionalPatches,
        isTemplateDuplication: true,
      });
    },
    [duplicate, recordTitle, recordUuid],
  );

  const handleDuplicate = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      if (!canDuplicate) {
        closeMenu();
        return;
      }

      if (canUseQuickLaunch && recordType) {
        openQuickLaunch({
          assetType: recordType,
          assetLabel,
          mode: 'duplicate',
          onSubmit: (prefill) => {
            runDuplicate(prefill).catch((error) => {
              console.error('[TemplatesRowActions] Duplicate error:', error);
            });
          },
        });
        closeMenu();
        return;
      }

      try {
        await runDuplicate();
      } finally {
        closeMenu();
      }
    },
    [
      assetLabel,
      canDuplicate,
      canUseQuickLaunch,
      closeMenu,
      openQuickLaunch,
      recordType,
      runDuplicate,
    ],
  );

  const handleUseTemplate = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      if (!canDuplicate) {
        closeMenu();
        return;
      }

      if (canUseQuickLaunch && recordType) {
        openQuickLaunch({
          assetType: recordType,
          assetLabel,
          mode: 'use-template',
          currentTitle: recordTitle,
          defaultWorkflowId: record?.workflow_id || undefined,
          defaultStartTime: record?.start_time || undefined,
          defaultSlug: record?.slug || undefined,
          onSubmit: (prefill) => {
            runUseTemplate(prefill).catch((error) => {
              console.error('[TemplatesRowActions] Use template error:', error);
            });
          },
        });
        closeMenu();
        return;
      }

      try {
        await runUseTemplate();
      } finally {
        closeMenu();
      }
    },
    [
      assetLabel,
      canDuplicate,
      canUseQuickLaunch,
      closeMenu,
      openQuickLaunch,
      record,
      recordTitle,
      recordType,
      runUseTemplate,
    ],
  );

  const handleDelete = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      if (!canDelete) {
        closeMenu();
        return;
      }
      setConfirmDeleteOpen(true);
      closeMenu();
    },
    [canDelete, closeMenu],
  );

  const handleDeleteCancel = useCallback(() => {
    setConfirmDeleteOpen(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!hasDeletePerm) {
      notify(t('blox.permissions.msg.required_short', { name: deletePermName }), {
        type: 'error',
      });
      setConfirmDeleteOpen(false);
      return;
    }

    if (!recordUuid) {
      console.warn('[TemplatesRowActions] No uuid found for deletion.');
      setConfirmDeleteOpen(false);
      return;
    }

    try {
      await mutateDelete({ uuid: [recordUuid] });
      notify('Delete request received.', { type: 'success' });
    } catch (e) {
      console.error('[TemplatesRowActions] Delete error:', e);
    } finally {
      setConfirmDeleteOpen(false);
    }
  }, [deletePermName, hasDeletePerm, mutateDelete, notify, recordUuid, t]);

  if (!recordUuid) {
    return null;
  }

  const showMenu = hasCreatePerm || hasDeletePerm;

  // Event stopper to prevent modal events from bubbling to the row
  const stopEventPropagation = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
  };

  return (
    <Fragment>
      {/* Wrap modals in a div to stop event propagation to the row */}
      <div
        onClick={stopEventPropagation}
        onKeyDown={stopEventPropagation}
      >
        <SimpleConfirmDeleteModal
          open={confirmDeleteOpen}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          initialWidth={350}
          initialHeight={215}
          isDraggable={true}
        />
        {quickLaunchModal}
      </div>
      <Box
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          justifyContent: 'flex-end',
        }}
      >
        {hasCreatePerm && (
          <Button
            data-testid="templates-row-actions-new-story-button"
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleUseTemplate}
            disableElevation={true}
            disabled={!canDuplicate}
            sx={{
              textTransform: 'none',
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              px: 1.5,
              py: 0.5,
              fontSize: bloxValues.tinyFontSizeRem,
            }}
          >
            {t('blox.editorial.templates.newStory', 'New Story')}
          </Button>
        )}
        {showMenu && (
          <>
            <Tooltip
              title={t('blox.editorial.moreActions.button')}
              disableInteractive
            >
              <span>
                <IconButton
                  data-testid="templates-row-actions-button"
                  onClick={openMenu}
                  size="small"
                >
                  <MoreVertIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Menu
              data-testid="templates-row-actions-menu"
              id={`${recordUuid}-actions-menu`}
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={closeMenu}
              slotProps={{
                list: {
                  'aria-labelledby': `${recordUuid}-actions-menu`,
                },
              }}
            >
              {hasCreatePerm ? (
                <MenuItem
                  data-testid="templates-row-actions-use-template"
                  onClick={handleUseTemplate}
                  disabled={!canDuplicate}
                >
                  <ContentCopyIcon sx={{ mr: 1 }} />
                  {t('blox.editorial.moreActions.useTemplate')}
                </MenuItem>
              ) : null}
              {hasCreatePerm ? (
                <MenuItem
                  data-testid="templates-row-actions-duplicate"
                  onClick={handleDuplicate}
                  disabled={!canDuplicate}
                >
                  <CopyAllIcon sx={{ mr: 1 }} />
                  {t('blox.actions.duplicate')}
                </MenuItem>
              ) : null}
              {hasDeletePerm ? (
                <MenuItem
                  data-testid="templates-row-actions-delete"
                  onClick={handleDelete}
                  disabled={!canDelete}
                >
                  <DeleteForeverIcon sx={{ mr: 1 }} />
                  {t('blox.actions.delete')}
                </MenuItem>
              ) : null}
            </Menu>
          </>
        )}
      </Box>
    </Fragment>
  );
});

TemplatesRowActions.displayName = 'TemplatesRowActions';

export default TemplatesRowActions;
